import {
  DialogRef,
  TMSScanOrderProductVo,
  TMSScanPalletProductVo,
  TMSShippingLabelMode,
} from "@/types";
import { Button } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useGlobalContext } from "@/hooks/context";
import { DeleteFilled, FileOutlined, PrinterOutlined } from "@ant-design/icons";
import { deleteScanOrderPalletCarton, OrderAPI } from "@/apis/tms/order";
import PdfViewerModal from "@/components/shared/PdfViewer";

interface PalletScannedProductsProps {
  className?: string;
  orderId: string;
  palletId: string;
  hasCartonLabel: boolean;
  shippingLabelMode: TMSShippingLabelMode;
  products?: TMSScanOrderProductVo[];
  scannedProducts: TMSScanPalletProductVo[];
  printCartonLabel: (
    orderId: string,
    palletId: string,
    scannedCartonId: number,
  ) => Promise<void>;
  onDeleteSuccess: () => void;
}

interface ScannedProductItem {
  palletCartonId: number;
  productName: string;
  productBarcode?: string;
  sku: string;
  serialNo?: string;
  sscc?: string;
}

const EmptyArray: ScannedProductItem[] = [];
export function PalletScannedProducts(props: PalletScannedProductsProps) {
  const {
    className,
    orderId,
    palletId,
    products,
    hasCartonLabel,
    shippingLabelMode,
    scannedProducts,
    printCartonLabel,
    onDeleteSuccess,
  } = props;

  const { hintPrompt, openConfirm } = useGlobalContext();
  const [printingLabel, setPrintingLabel] = useState(false);
  const pdfViewerModalRef = useRef<DialogRef>(null);

  const items = useMemo(() => {
    if (products == null) return EmptyArray;
    return scannedProducts.map((scanned) => {
      const product = products.find((p) => p.id === scanned.productId);
      if (product == null)
        throw new Error(`Product not found for scanned product ${scanned.id}`);
      return {
        palletCartonId: scanned.id,
        productName: product.productName,
        productBarcode: product.productBarcode,
        sku: product.productNo,
        serialNo: scanned.serialNo,
        sscc: scanned.sscc,
      };
    });
  }, [products, scannedProducts]);

  const clickPrintCartonLabel = useCallback(
    (scannedCartonId: number) => {
      setPrintingLabel(true);
      printCartonLabel(orderId, palletId, scannedCartonId).finally(() => {
        setPrintingLabel(false);
      });
    },
    [orderId, palletId, printCartonLabel],
  );

  const confirmDelete = useCallback(
    (scannedCartonId: number) => {
      openConfirm(
        "Are you sure you want to delete this scanned carton?",
        () => {
          return deleteScanOrderPalletCarton(
            orderId,
            palletId,
            scannedCartonId,
          );
        },
        () => {
          hintPrompt("success", "Success", "Carton deleted successfully");
          onDeleteSuccess();
        },
        (error: any) => {
          hintPrompt("error", "Error", error.msg ?? "Unknown error");
        },
      )
    },
    [openConfirm, hintPrompt, orderId, palletId, onDeleteSuccess],
  );

  return (
    <div className={classNames(className)}>
      {items.map((product, index) => (
        <div
          key={index}
          className={classNames(
            "flex flex-row justify-between items-center",
            index === items.length - 1
              ? "border-b-0"
              : "border-b-[1px] border-gray-200",
          )}
        >
          <div className={classNames("flex flex-col px-4 py-2")}>
            <div className="truncate text-gray-500 text-wrap break-all">
              {product.productName}
            </div>
            <div className="flex flex-row gap-2 items-center text-[12px] leading-tight">
              <span className="text-gray-500 italic">SKU:</span>
              <span className="font-bold text-[12px]">{product.sku}</span>
            </div>
            <div className="flex flex-row gap-2 items-center text-[12px] leading-tight">
              <div className="text-gray-500 italic">SSCC:</div>
              <div className="text-[12px]">{product?.sscc ?? "N/A"}</div>
            </div>
            <div className="flex flex-row gap-2 items-center text-[12px] leading-tight">
              <div className="text-gray-500 italic">S/N:</div>
              <div className="text-[12px]">{product?.serialNo ?? "N/A"}</div>
            </div>
          </div>
          <div className="flex flex-row gap-1">
            <div className="flex flex-col gap-2">
              <Button
                icon={<FileOutlined />}
                color="blue"
                variant="outlined"
                className="!px-2 !gap-1"
                loading={printingLabel}
                disabled={!hasCartonLabel}
                onClick={() => {
                  const params = [
                    "scan",
                    orderId,
                    "pallets",
                    palletId,
                    "cartons",
                    product.palletCartonId,
                    "label",
                  ].join("/");
                  pdfViewerModalRef.current?.showModal({
                    url: `${OrderAPI.config.baseURL}/${params}`,
                  });
                }}
              >
                <span className="w-[57px]">Carton</span>
              </Button>
              <Button
                icon={<FileOutlined />}
                color="blue"
                variant="outlined"
                className="!px-2 !gap-1"
                loading={printingLabel}
                disabled={shippingLabelMode !== TMSShippingLabelMode.Carton}
                onClick={() => {
                  const params = [
                    `orderIdOrNo=${orderId}`,
                    `palletId=${palletId}`,
                    `palletProductId=${product.palletCartonId}`,
                  ].join("&");
                  pdfViewerModalRef.current?.showModal({
                    url: `${OrderAPI.config.baseURL}/scan/${orderId}/shipping/label.pdf?${params}`,
                  });
                }}
              >
                <span className="w-[57px]">Shipping</span>
              </Button>
            </div>
            <div className="flex flex-col gap-2 mr-4">
              <Button
                icon={<PrinterOutlined />}
                color="green"
                variant="outlined"
                loading={printingLabel}
                onClick={() => clickPrintCartonLabel(product.palletCartonId)}
              />
              <Button
                danger
                icon={<DeleteFilled />}
                loading={printingLabel}
                onClick={() => confirmDelete(product.palletCartonId)}
              />
            </div>
          </div>
        </div>
      ))}
      <PdfViewerModal ref={pdfViewerModalRef} />
    </div>
  );
}
